import React, { useEffect, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import Routes from './router/index.jsx';
import { getProfile, hasAuthenticated } from 'reducers/auth';
import PropTypes from 'prop-types';
import {
  fetchBillingInfo,
  fetchSecurityCompanyDetail,
  fetchSecurityCompanyServices,
  getSecurityCompanyDetail,
  getSecurityCompanyServices,
  getSitesEnabled,
} from 'reducers/securityCompany';
import {
  getGeneralInfo,
  getSecurityId,
  getUserRoles,
  getRegionId,
} from 'reducers/users';
import { SECURITY_COMPANY_FULL_ACCESS } from 'utils/constants/securityCompany';
import { getCurrentPage, updatePage } from 'reducers/ui';
import { fetchGlobalList } from 'reducers/globalList';
import { actions as cleaningActions } from 'containers/Pages/Tasks/CleanerTasks/Modules/dataGridTable';
import { actions as personnelActions } from 'containers/Pages/People/Personnel/Modules/dataGridTable';
import { serviceTypes } from 'utils/constants/serviceTypes.js';
import { TASK_TYPE } from 'utils/constants/task';
import { PAGE_URLS } from 'utils/constants/index.js';
import { ROLES } from 'utils/constants/roles';
import { siteDataGridActions } from 'containers/Pages/Locations/Sites';
import { workOrderGridTableActions } from 'containers/Pages/Tasks/WorkOrders';
import { eventsGridActions } from 'containers/Pages/Events';
import { securityGridTableActions } from 'containers/Pages/Tasks/Security';
import { emergencyDataGridAction } from 'containers/Pages/Tasks/Emergencies';
import LinearProgress from '@material-ui/core/LinearProgress';
import isEmpty from 'lodash/isEmpty';
import {
  getSecurityCompanyBillingInfo,
  getCompanyThemes,
} from './reducers/securityCompany/index';
import { actions as themeActions } from 'slices/themes';
import { removeEditFormStateKeys } from 'utils/valueChecker';
import { zoneActions } from 'containers/Pages/Locations/Zones';
import {
  regionActions,
  regionPageActions,
} from 'containers/Pages/Region/Region';
const { setGlobalTheme } = themeActions;

const themeKey = 'theme';

//NOTE: For developers:React 18 and up
// Returning something from useEffect that was neither a function nor undefined will crash the app
// and produce 'destroy is not function' error in console. There are other reasons that can produce this error
// like e.g. useeffect cannot be async
// const myFunction = async () => {
//      run asynchronous tasks here
// };

// it should be
// const myFunction = async () => {
// run asynchronous tasks here
// };

// useEffect(() => {
//     myFunction();
// }); // Note the curly braces around myFunction!
// useEffect(() => myFunction());

//You can't return anything else e.g. useEffect(() => null) would produce such an error.
// Warning: useEffect must not return anything besides a function, which is used for clean-up.
// If your effect does not require clean up, return undefined (or nothing).

const App = ({
  fetchProfile,
  authenticated,
  fetchSecurityCompanyDetail,
  companyId,
  generalInfo,
  companyDetail,
  fetchSecurityCompanyServices,
  roles,
  services,
  currentUrl,
  regionId,
  areSitesEnabled,
  fetchBillingInfo,
  billingInfo,
  themes,
}) => {
  const dispatch = useDispatch();
  const changeTheme = useCallback(
    mode => {
      dispatch(setGlobalTheme(mode));
    },
    [dispatch],
  );
  const dispatchCleaningListAction = useCallback(
    payload => {
      dispatch(cleaningActions.fetchGridList(payload));
    },
    [dispatch],
  );

  const dispatchFetchZones = useCallback(() => {
    dispatch(zoneActions.fetchGridList({ fetchAll: true }));
  }, [dispatch]);
  const dispatchFetchSites = useCallback(() => {
    dispatch(siteDataGridActions.fetchGridList({ fetchAll: true }));
  }, [dispatch]);
  const dispatchEmergencyListAction = useCallback(
    payload => {
      dispatch(emergencyDataGridAction.fetchGridList(payload));
    },
    [dispatch],
  );
  const dispatchSecurityListAction = useCallback(
    payload => {
      dispatch(securityGridTableActions.fetchGridList(payload));
    },
    [dispatch],
  );
  const dispatchWorkOderListAction = useCallback(
    payload => {
      dispatch(workOrderGridTableActions.fetchGridList(payload));
    },
    [dispatch],
  );

  const dispatchPersonnelListAction = useCallback(
    filters => {
      dispatch(personnelActions.fetchGridList(filters));
    },
    [dispatch],
  );
  const dispatchEventListAction = useCallback(() => {
    dispatch(eventsGridActions.fetchGridList({}));
  }, [dispatch]);

  const dispatchFetchGlobalList = useCallback(
    ({ category }) => dispatch(fetchGlobalList({ category })),
    [dispatch],
  );
  const dispatchFetchRegion = useCallback(
    regionId => {
      dispatch(regionPageActions.fetchUsersRegion({ id: regionId }));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [dispatch],
  );
  const dispatchFetchRegions = useCallback(() => {
    dispatch(regionActions.fetchGridList({}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);
  const dispatchSiteListAction = useCallback(() => {
    dispatch(siteDataGridActions.fetchGridList({}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const getProfile = useCallback(() => {
    if (!generalInfo.userId) {
      fetchProfile();
    }
  }, [fetchProfile, generalInfo.userId]);

  const getCompanyDetails = useCallback(() => {
    if (authenticated && companyId && !companyDetail.id) {
      if (SECURITY_COMPANY_FULL_ACCESS.some(item => roles.includes(item))) {
        fetchSecurityCompanyDetail({
          id: companyId,
        });
      } else {
        fetchSecurityCompanyServices({ id: companyId });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    authenticated,
    fetchSecurityCompanyDetail,
    companyId,
    companyDetail,
    fetchSecurityCompanyServices,
    roles,
  ]);

  useEffect(() => {
    // if profile exist then dont call it again

    if (!generalInfo.id) {
      // fetch profile only when there is no user info
      getProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generalInfo.userId, authenticated]);

  useEffect(() => {
    getCompanyDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyDetail, getCompanyDetails]);

  useEffect(() => {
    // These requests will populate the maps and should only be called if the user is not on subsequent page.
    //fetch Cleaning tasks if not on cleaning page and has cleaning subscribed as the request will be handled by the cleaning component
    if (!authenticated) return;
    if (
      services.includes(serviceTypes.cleaning) &&
      PAGE_URLS.CLEANING_TASKS.link !== currentUrl
    ) {
      dispatchCleaningListAction({
        filters: {
          status: [
            {
              id: 'NEW',
              name: 'New',
            },
            {
              id: 'IN_PROGRESS',
              name: 'In-Progress',
            },
          ],
          taskCategory: [TASK_TYPE.CLEANING.id],
        },
      });
    }
    if (
      services.includes(serviceTypes.security) &&
      PAGE_URLS.SECURITY_TASKS.link !== currentUrl
    ) {
      dispatchSecurityListAction({
        filters: {
          status: [
            {
              id: 'NEW',
              name: 'New',
            },
            {
              id: 'IN_PROGRESS',
              name: 'In-Progress',
            },
          ],
          taskCategory: [TASK_TYPE.SECURITY.id],
        },
      });
    }
    if (
      services.includes(serviceTypes.emergency.standard) &&
      PAGE_URLS.EMERGENCIES.link !== currentUrl
    ) {
      dispatchEmergencyListAction({
        filters: {},
      });
    }
    if (
      services.includes(serviceTypes.maintenance) &&
      PAGE_URLS.WORK_ORDERS.link !== currentUrl
    ) {
      dispatchWorkOderListAction({
        filters: {
          status: [
            {
              id: 'NEW',
              name: 'New',
            },
            {
              id: 'IN_PROGRESS',
              name: 'In-Progress',
            },
          ],
          taskCategory: [TASK_TYPE.MAINTENANCE.id],
        },
      });
    }
    if (
      PAGE_URLS.EVENTS.link !== currentUrl &&
      services.length > 0 &&
      authenticated
    ) {
      dispatchEventListAction();
    }
    if (areSitesEnabled) {
      dispatchSiteListAction();
    }

    if (
      services.length > 0 &&
      PAGE_URLS.PERSONNEL.link !== currentUrl &&
      authenticated
    ) {
      // fetch personnels if not on personnel page, as the request will be handled by the personnel component
      dispatchPersonnelListAction({
        filters: {
          role: [
            'CLEANER',
            'DRIVER',
            'GUARD',
            'MANAGER',
            'RESPONDER',
            'SUPERVISOR',
          ],
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated, services.length > 0]);

  useEffect(() => {
    if (roles && roles.includes(ROLES.SECURITY_COMPANY_MANAGER.id)) {
      // billing info can only be fetched by security company manager
      fetchBillingInfo();
    }
    removeEditFormStateKeys(); // remove all the storage keys on intial loading
  }, [fetchBillingInfo, companyId, roles]);

  useEffect(() => {
    if (
      authenticated &&
      !roles.includes(ROLES.SRM_EMPLOYEE.id) &&
      !roles.includes(ROLES.SRM_ADMIN.id)
    ) {
      dispatchFetchGlobalList({ category: 'departments' });
      dispatchFetchGlobalList({ category: 'sites' });
      dispatchFetchGlobalList({ category: 'businessClients' });
      dispatchFetchGlobalList({ category: 'users' });
      dispatchFetchRegion(regionId);
      dispatchFetchZones();
      dispatchFetchSites();
    } else if (authenticated && roles.includes(ROLES.SRM_EMPLOYEE.id)) {
      dispatchFetchRegions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated, roles, regionId]);

  useEffect(() => {
    if (isEmpty(themes)) {
      return;
    }
    if (themes.length === 1) {
      //set the theme to their only theme
      changeTheme(themes[0]);
      //clear the storage just in case
      localStorage.removeItem(themeKey);
    }
    const item = localStorage.getItem(themeKey);
    if (item) {
      //if the theme is not in the list of themes ignore it
      if (themes.some(t => item === t)) {
        changeTheme(item);
      } else {
        //set it to blank and don't change
        localStorage.removeItem(themeKey);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themes]);

  const renderRoutes = () => {
    if (roles && roles.includes(ROLES.SECURITY_COMPANY_MANAGER.id)) {
      if (!isEmpty(billingInfo) && !isEmpty(themes)) {
        // only load routes when billing info is available if the user is account holder
        return <Routes />;
      }
      return <LinearProgress />;
    } else if (typeof authenticated === 'boolean') {
      return <Routes />;
    } else {
      return <LinearProgress />;
    }
  };

  return companyDetail && renderRoutes(); // only render routes once we have company info
};
App.propTypes = {
  fetchProfile: PropTypes.func,
  authenticated: PropTypes.bool,
  areSitesEnabled: PropTypes.bool,
  generalInfo: PropTypes.object,
  fetchSecurityCompanyDetail: PropTypes.func,
  companyId: PropTypes.string,
  regionId: PropTypes.string,
  currentUrl: PropTypes.string,
  companyDetail: PropTypes.object,
  billingInfo: PropTypes.object,
  fetchSecurityCompanyServices: PropTypes.func,
  updatePage: PropTypes.func,
  fetchBillingInfo: PropTypes.func,
  roles: PropTypes.array,
  services: PropTypes.array,
};
const mapStateToProps = state => ({
  authenticated: hasAuthenticated(state),
  companyId: getSecurityId(state),
  companyDetail: getSecurityCompanyDetail(state),
  generalInfo: getGeneralInfo(state),
  roles: getUserRoles(state),
  services: getSecurityCompanyServices(state),
  currentUrl: getCurrentPage(state),
  regionId: getRegionId(state),
  areSitesEnabled: getSitesEnabled(state),
  billingInfo: getSecurityCompanyBillingInfo(state),
  themes: getCompanyThemes(state),
});

const mapDispatchToProps = dispatch => ({
  fetchProfile: () => dispatch(getProfile()),
  fetchSecurityCompanyDetail: ({ id }) =>
    dispatch(fetchSecurityCompanyDetail(id)),
  fetchSecurityCompanyServices: ({ id }) =>
    dispatch(fetchSecurityCompanyServices(id)),
  updatePage: location => dispatch(updatePage(location)),
  fetchBillingInfo: () => dispatch(fetchBillingInfo()),
});

export default connect(mapStateToProps, mapDispatchToProps)(App);
